import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { readAccountAchievements, searchAccounts } from '../api/endpoints';
import type { AccountAchievements, AdminAccount, Paginated } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { AccountActions } from '../components/AccountActions';
import { CreateAccountDialog } from '../components/CreateAccountDialog';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { TableFilters } from '../components/TableFilters';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { useSort } from '../components/useSort';

/** The columns the API will order by. It matches these against a fixed list; anything else is ignored. */
type SortKey = 'email' | 'displayName' | 'role' | 'status' | 'locale' | 'createdAt' | 'lastLoginAt';

const ROLES = ['PARENT', 'SUPPORT', 'CONTENT_EDITOR', 'SUPERUSER'];
const STATUSES = ['ACTIVE', 'SUSPENDED'];
const LOCALES = ['sk', 'en'];

const ROLE_COLOUR: Record<string, 'default' | 'primary' | 'secondary'> = {
  PARENT: 'default',
  SUPPORT: 'primary',
  CONTENT_EDITOR: 'primary',
  SUPERUSER: 'secondary',
};

export const AccountsPage = () => {
  const { account: actor } = useAuth();
  // Only a superuser may act, and the API enforces it. Hiding the control avoids offering a button whose
  // only outcome is 403 — the UI gate is manners, not security (R0004).
  const canManage = actor?.role === 'SUPERUSER';
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [locale, setLocale] = useState('');
  const [inspected, setInspected] = useState<AdminAccount | null>(null);
  const [creating, setCreating] = useState(false);
  const sort = useSort<SortKey>('createdAt', 'desc', () => setPage(0));
  const { data, error, isLoading, refetch } = useQuery<Paginated<AdminAccount>>({
    queryKey: ['accounts', term, page, sort.key, sort.direction, role, status, locale],
    queryFn: () =>
      searchAccounts(
        term,
        page + 1,
        sort.key,
        sort.direction,
        role || undefined,
        status || undefined,
        locale || undefined,
      ),
  });

  const items = data?.items ?? [];

  // The family, not one child: a parent writes in about their household, and the first question support
  // gets asked is which of their children is doing what.
  const family = useQuery<AccountAchievements[]>({
    queryKey: ['account-achievements', inspected?.id],
    queryFn: () => readAccountAchievements(inspected!.id),
    enabled: inspected !== null,
  });

  return (
    <>
      <PageHeader
        title="Účty"
        subtitle={
          canManage
            ? 'Rodičovské aj prevádzkové účty. Superužívateľ môže odtiaľto vytvoriť účet, nastaviť heslo, pozastaviť účet alebo zmeniť rolu — každá z týchto akcií sa zapisuje do auditu.'
            : 'Rodičovské aj prevádzkové účty, na čítanie. Vytvoriť účet, nastaviť heslo, pozastaviť účet alebo zmeniť rolu môže iba superužívateľ.'
        }
        action={
          canManage ? (
            <Button variant="contained" onClick={() => setCreating(true)}>
              Nový účet
            </Button>
          ) : undefined
        }
      />

      <CreateAccountDialog open={creating} onClose={() => setCreating(false)} />

      <ErrorNotice error={error} onRetry={() => void refetch()} />

      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label="Hľadať e-mail alebo meno"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 260 }}
          />
          <TextField
            size="small"
            select
            label="Rola"
            value={role}
            onChange={(event) => {
              setRole(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {ROLES.map((one) => (
              <MenuItem key={one} value={one}>
                {one}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Stav"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {STATUSES.map((one) => (
              <MenuItem key={one} value={one}>
                {one}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Jazyk"
            value={locale}
            onChange={(event) => {
              setLocale(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {LOCALES.map((one) => (
              <MenuItem key={one} value={one}>
                {one}
              </MenuItem>
            ))}
          </TextField>
        </TableFilters>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="email">
                  E-mail
                </SortableCell>
                <SortableCell sort={sort} column="displayName">
                  Meno
                </SortableCell>
                <SortableCell sort={sort} column="role">
                  Rola
                </SortableCell>
                <SortableCell sort={sort} column="status">
                  Stav
                </SortableCell>
                <SortableCell sort={sort} column="locale">
                  Jazyk
                </SortableCell>
                <SortableCell sort={sort} column="createdAt">
                  Vytvorený
                </SortableCell>
                <SortableCell sort={sort} column="lastLoginAt">
                  Posledné prihlásenie
                </SortableCell>
                {/* Actions are buttons, not a value. There is nothing to order by. */}
                <TableCell align="right">Akcie</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? <TableSkeletonRows columns={8} /> : null}
              {!isLoading &&
                items.map((account: AdminAccount) => (
                  <TableRow
                    key={account.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setInspected(account)}
                  >
                    <TableCell sx={{ fontWeight: 560 }}>{account.email}</TableCell>
                    <TableCell>
                      {account.displayName ?? (
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          —
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={account.role}
                        color={ROLE_COLOUR[account.role] ?? 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={account.status}
                        color={account.status === 'ACTIVE' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{account.locale}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {new Date(account.createdAt).toLocaleDateString('sk-SK')}
                    </TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {account.lastLoginAt ? (
                        new Date(account.lastLoginAt).toLocaleString('sk-SK')
                      ) : (
                        <Typography variant="body2" component="span" color="text.disabled">
                          nikdy
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      {canManage ? <AccountActions account={account} actorId={actor?.id ?? null} /> : null}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && items.length === 0 ? (
          <EmptyState
            title={term ? 'Žiadny účet nezodpovedá hľadaniu' : 'Zatiaľ tu nie sú žiadne účty'}
            detail={term ? `Skúste inú časť e-mailovej adresy než „${term}".` : undefined}
          />
        ) : null}

        <TablePagination
          component="div"
          count={data?.total ?? 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={data?.pageSize ?? 25}
          rowsPerPageOptions={[data?.pageSize ?? 25]}
          labelRowsPerPage="Na stranu"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </Paper>

      <Dialog open={inspected !== null} onClose={() => setInspected(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {inspected?.email}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Deti účtu a ich ocenenia
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {family.isLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
          <ErrorNotice error={family.error} onRetry={() => void family.refetch()} />

          {family.data && family.data.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              Tento účet zatiaľ nemá žiadny profil dieťaťa.
            </Typography>
          ) : null}

          <Table size="small">
            <TableBody>
              {(family.data ?? []).map((child: AccountAchievements) => (
                <TableRow key={child.childId}>
                  <TableCell sx={{ fontWeight: 560, borderBottom: 'none' }}>{child.nickname}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', borderBottom: 'none' }}>
                    {child.earnedCount} / {child.totalCount}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', borderBottom: 'none' }}
                  >
                    {child.lastEarnedAt ? (
                      new Date(child.lastEarnedAt).toLocaleDateString('sk-SK')
                    ) : (
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        zatiaľ nič
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Podrobnosti o jednom dieťati sú na stránke Deti. Ocenenia sa nedajú udeliť ani odobrať ručne —
            vyplývajú z toho, čo dieťa naozaj urobilo.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};
